import {
	QueryClient,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from '@tanstack/react-query';
import axios from 'axios';
import {
	CATCHUP_API_MUTATION__ISSUE_ERROR_TOAST,
	CATCHUP_API_MUTATION__ISSUE_SUCCESS_TOAST,
	CATCHUP_PAGE__INVALIDATE_ACTION__ERROR_TOAST,
	CATCHUP_PAGE__INVALIDATE_ACTION__SUCCESS_TOAST,
	CATCHUP_PAGE__WRITEOFF_ACTION__ERROR_TOAST,
	CATCHUP_PAGE__WRITEOFF_ACTION__SUCCESS_TOAST,
} from '../components/catchUps/data/constants';
import { errorToast, successToast } from '../components/Toast';
import {
	CatchUpInvoice,
	CatchUpInvoiceIssueRequest,
	CatchUpInvoiceResponse,
	CatchUpRequest,
} from '../types/catchUps';
import { getApiUrl } from '../util/util';
import { get, patch, post } from './http';

const CatchUpsApi = {
	getCatchUps(
		authorizationToken: string,
		pagingToken?: string,
		pageSize?: number,
		policyholderLocator?: string,
		policyLocator?: string,
		state?: string,
	) {
		const params = new URLSearchParams();
		if (pagingToken) {
			params.append('pagingToken', pagingToken);
		}
		if (pageSize && !pagingToken) {
			params.append('pageSize', pageSize.toString());
		}

		if (policyholderLocator && !pagingToken) {
			params.append('policyholderLocator', policyholderLocator);
		}

		if (policyLocator && !pagingToken) {
			params.append('policyLocator', policyLocator);
		}

		if (state && !pagingToken) {
			params.append('state', state);
		}

		const url = `${getApiUrl()}/catchUps?${params.toString()}`;
		return get<CatchUpInvoiceResponse>(url, authorizationToken);
	},
	getCatchUp(authorizationToken: string, catchUpLocator: string) {
		return get<CatchUpInvoice>(
			`${getApiUrl()}/catchUps/${catchUpLocator}`,
			authorizationToken,
		);
	},
	issueCatchUp(
		authorizationToken: string,
		cashBalanceLocator: string,
		catchUpRequest: CatchUpInvoiceIssueRequest,
	) {
		return post<CatchUpInvoice>(
			`${getApiUrl()}/cashBalances/${cashBalanceLocator}/invoices`,
			authorizationToken,
			catchUpRequest,
		);
	},
	writeOffInvoice(authorizationToken: string, catchUpInvoiceLocator: string) {
		return patch<CatchUpInvoice>(
			`${getApiUrl()}/catchUps/${catchUpInvoiceLocator}/writeOff`,
			authorizationToken,
		);
	},
	invalidateInvoice(authorizationToken: string, catchUpInvoiceLocator: string) {
		return patch<CatchUpInvoice>(
			`${getApiUrl()}/catchUps/${catchUpInvoiceLocator}/invalidate`,
			authorizationToken,
		);
	},
};

export const useIssueCatchUpMutation = ({
	authToken,
	queryClient,
}: {
	authToken: string;
	queryClient: QueryClient;
}) =>
	useMutation({
		mutationFn: (catchUp: CatchUpRequest) =>
			CatchUpsApi.issueCatchUp(
				authToken,
				catchUp.locator, // cash balance locator
				catchUp.request as CatchUpInvoiceIssueRequest,
			),
		onSuccess: async (newCatchUp, createRequest) => {
			queryClient.invalidateQueries(
				getCatchUpQueryKey(authToken, newCatchUp.data.locator),
			);
			successToast(CATCHUP_API_MUTATION__ISSUE_SUCCESS_TOAST);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					CATCHUP_API_MUTATION__ISSUE_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const useWriteOffInvoiceMutation = (
	authToken: string,
	queryClient: QueryClient,
) => {
	return useMutation({
		mutationFn: ({ invoiceLocator }: { invoiceLocator: string }) =>
			CatchUpsApi.writeOffInvoice(authToken, invoiceLocator),
		onSuccess: (response) => {
			const queryKey = getCatchUpQueryKey(authToken, response.data.locator);
			queryClient.invalidateQueries(queryKey);
			successToast(CATCHUP_PAGE__WRITEOFF_ACTION__SUCCESS_TOAST);
		},
		onError: () => errorToast(CATCHUP_PAGE__WRITEOFF_ACTION__ERROR_TOAST),
	});
};

export const useInvalidateInvoiceMutation = (
	authToken: string,
	queryClient: QueryClient,
) => {
	return useMutation({
		mutationFn: ({ invoiceLocator }: { invoiceLocator: string }) =>
			CatchUpsApi.invalidateInvoice(authToken, invoiceLocator),
		onSuccess: (response) => {
			const queryKey = getCatchUpQueryKey(authToken, response.data.locator);
			queryClient.invalidateQueries(queryKey);
			successToast(CATCHUP_PAGE__INVALIDATE_ACTION__SUCCESS_TOAST);
		},
		onError: () => errorToast(CATCHUP_PAGE__INVALIDATE_ACTION__ERROR_TOAST),
	});
};

export const getPaginatedCatchUpsQueryKey = (
	pageSize: number,
	token: string,
	policyholderLocator?: string,
	policyLocator?: string,
) => ['catchUps', pageSize, token, policyholderLocator, policyLocator];

export const usePaginatedCatchUps = (
	pageSize: number,
	token: string,
	policyholderLocator?: string,
	policyLocator?: string,
) => {
	return useInfiniteQuery(
		getPaginatedCatchUpsQueryKey(
			pageSize,
			token,
			policyholderLocator,
			policyLocator,
		),
		async ({ pageParam = 0 }) => {
			const response = await CatchUpsApi.getCatchUps(
				token,
				pageParam,
				pageSize,
				policyholderLocator,
				policyLocator,
			);
			return response;
		},
		{
			getNextPageParam: (lastPage) =>
				lastPage.data.pagingToken === ''
					? undefined
					: lastPage.data.pagingToken,
			keepPreviousData: true,
		},
	);
};

export const getCatchUpQueryKey = (
	catchUpLocator: string,
	authToken: string,
) => ['catchUps', catchUpLocator, authToken];

export const useCatchUp = (
	catchUpLocator: string,
	authToken: string,
	cashManagementEnabled = false,
	hasFetchPermission = false,
) => {
	return useQuery({
		queryKey: getCatchUpQueryKey(catchUpLocator, authToken),
		queryFn: async () => {
			const response = await CatchUpsApi.getCatchUp(authToken, catchUpLocator);
			return response.data;
		},
		enabled: !!catchUpLocator && cashManagementEnabled && hasFetchPermission,
	});
};

export default CatchUpsApi;
